import React, {useState} from 'react';
import {Pressable, StyleSheet, View} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useTranslation} from 'react-i18next';

import {useAuthStore} from '@/entities/session/auth-store';
import {contextToneAndLabel} from '@/entities/session/labels';
import type {AppContext} from '@/entities/session/contexts';
import {Icon} from '@/shared/ui/icons';
import {Logo} from '@/shared/ui/logo';
import {Sheet} from '@/shared/ui/sheet';
import {StatusDot} from '@/shared/ui/status';
import {AppText} from '@/shared/ui/text';
import {controls, layout, radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Полоса над рабочей областью — только на телефоне, и только с тем, чего нет в теле
 * страницы.
 *
 * ## Что она перестала делать
 *
 * Печатать имя раздела. Раньше здесь стояло название ВКЛАДКИ, а на 40pt ниже, уже в
 * теле, — название ЭКРАНА, и совпадали они далеко не всегда: над «Журналом событий»
 * было написано «Ещё», над карточкой человека — «Отчёты». Два заголовка, из которых
 * верхний врёт, — это хуже, чем один. Имя экрана теперь живёт в теле страницы (см.
 * `shared/ui/page`), и здесь его нет.
 *
 * Осталось ровно два факта, которых в теле нет и быть не может:
 *
 *      SAMVISION                       ● ООО «Пример» ⌄
 *
 * марка — чтобы приложение, поставленное на домашний экран, называло себя, и чип
 * контекста — потому что «чьи двери я сейчас открою?» не должно быть вопросом, ответ
 * на который надо идти искать. Чип нажимается только когда есть куда переключаться.
 *
 * На десктопе этой полосы нет вовсе: бренд, разделы, контекст и аккаунт держит боковая
 * панель, и вторая горизонтальная полоса над ней повторяла бы её содержимое.
 */
const styles = StyleSheet.create({
  bar: {borderBottomWidth: StyleSheet.hairlineWidth},
  inner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    width: '100%',
    maxWidth: layout.maxContentWidth,
    alignSelf: 'center',
    height: 52,
    paddingHorizontal: layout.gutter,
  },
  spacer: {flex: 1},
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(2),
    maxWidth: 200,
    paddingHorizontal: spacing(3),
    paddingVertical: spacing(2),
    borderRadius: radii.md,
    borderWidth: StyleSheet.hairlineWidth,
    /* Эта полоса живёт только на телефоне, и это единственный контрол в ней. В вебе
       `hitSlop` не работает (react-native-web его не реализует), поэтому цель равна
       нарисованной коробке — и коробка держит 44pt. В 52pt полосу она входит. */
    minHeight: controls.lg,
  },
  chipLabel: {flexShrink: 1},
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing(3),
    padding: spacing(4),
    borderWidth: StyleSheet.hairlineWidth,
    borderRadius: radii.lg,
  },
  optionText: {flex: 1, gap: 2},
  check: {
    width: 22,
    height: 22,
    borderRadius: radii.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

/** Курсор над контролом или палец на нём. */
function isHot(state: {pressed: boolean}): boolean {
  return state.pressed || (state as {hovered?: boolean}).hovered === true;
}

export function AppHeader() {
  const {t} = useTranslation();
  const p = useTheme();
  const insets = useSafeAreaInsets();

  const contexts = useAuthStore(s => s.contexts);
  const active = useAuthStore(s => s.activeContext);
  const setActiveContext = useAuthStore(s => s.setActiveContext);

  const [open, setOpen] = useState(false);
  const switchable = contexts.length > 1;
  const badge = active ? contextToneAndLabel(active) : null;

  function choose(context: AppContext) {
    setActiveContext(context);
    setOpen(false);
  }

  return (
    <View
      style={[
        styles.bar,
        {
          backgroundColor: p.chrome,
          borderBottomColor: p.chromeBorder,
          paddingTop: insets.top,
        },
      ]}>
      <View style={styles.inner}>
        <Logo width={92} />
        <View style={styles.spacer} />

        {active && badge ? (
          <Pressable
            testID="context-chip"
            accessibilityRole={switchable ? 'button' : 'text'}
            accessibilityLabel={
              switchable ? t('context.switch') : `${t(badge.kindKey)}: ${active.title}`
            }
            disabled={!switchable}
            onPress={() => setOpen(true)}
            style={state => [
              styles.chip,
              {
                backgroundColor: isHot(state) ? p.chromeHover : p.chromeSunken,
                borderColor: p.chromeBorder,
              },
            ]}>
            <StatusDot tone={badge.tone} size={8} />
            <AppText
              variant="caption"
              color={p.chromeForeground}
              numberOfLines={1}
              style={styles.chipLabel}>
              {active.title}
            </AppText>
            {switchable ? <Icon name="updown" size={14} color={p.chromeMuted} /> : null}
          </Pressable>
        ) : null}
      </View>

      <Sheet
        testID="context-sheet"
        visible={open}
        onClose={() => setOpen(false)}
        title={t('context.title')}>
        {contexts.map(c => {
          const b = contextToneAndLabel(c);
          const isActive = active?.kind === c.kind && active?.companyId === c.companyId;
          return (
            <Pressable
              key={`${c.kind}:${c.companyId}`}
              testID={`context-option-${c.kind}`}
              accessibilityRole="button"
              accessibilityState={{selected: isActive}}
              onPress={() => choose(c)}
              style={({pressed}) => [
                styles.option,
                {
                  backgroundColor: isActive ? p.accent : pressed ? p.muted : p.card,
                  borderColor: isActive ? p.primary : p.border,
                },
              ]}>
              <StatusDot tone={b.tone} size={9} />
              <View style={styles.optionText}>
                <AppText variant="subtitle">{c.title}</AppText>
                <AppText variant="caption">
                  {t(b.kindKey)}
                  {c.kind === 'company' ? ` · ${t(`context.role.${c.role}`)}` : ''}
                </AppText>
              </View>
              {/* Выбранное сказано дважды — заливкой И галочкой. Одного шага серого
                  мало, галочка и есть ответ. */}
              {isActive ? (
                <View style={[styles.check, {backgroundColor: p.primary}]}>
                  <Icon name="check" size={14} color={p.primaryForeground} />
                </View>
              ) : null}
            </Pressable>
          );
        })}
      </Sheet>
    </View>
  );
}
